import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, MessageCircle } from 'lucide-react';
import type {
  EventMapping,
  MessageStatus,
  MessagingConnection,
  NotificationEvent,
  ReminderSettings,
} from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { ConnectionStatusBadge } from '../components/notifications/connection-card.js';
import { EventMappingDialog } from '../components/notifications/event-mapping-dialog.js';
import { MessageLogTable } from '../components/notifications/message-log-table.js';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  ListSkeleton,
  useToast,
} from '@tailoring-crm/ui';
import {
  useEventPreview,
  useMessageLog,
  useMessageTemplates,
  useMessagingSettings,
  useRetryMessage,
  useSaveEventMapping,
  useSaveMessagingPreferences,
  useSendTestMessage,
} from '../lib/notifications/use-notifications.js';

/**
 * WhatsApp settings: what the shop says to its customers, and what it has already said.
 *
 * The workspace itself is NOT here. Connecting happens once, on the Integrations screen, and
 * then never again; what a shop tells its customers is edited for as long as the shop exists.
 * Keeping a credential field at the top of a screen people visit weekly invited a second
 * paste of a key that was already working.
 *
 * What stays is a strip — connected or not, the number messages leave from, and the way back
 * to the screen that owns it — because everything below still depends on that answer. An
 * unconnected shop's event rows are disabled, and a disabled row with no explanation on the
 * screen reads as a broken product rather than an unfinished setup.
 *
 * Then the notification list, where each row is one thing the shop might tell a customer and
 * each is switched on independently, because most shops want "your order is ready" long before
 * they want a balance reminder. The log last: it is the screen people come back to, and by
 * then they know where it is.
 */
export function NotificationsPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();

  const settings = useMessagingSettings();
  const connection = settings.data?.connection;
  const connected = connection !== undefined && connection.status !== 'not_connected';

  // The provider is only asked for templates once there is a key to ask with.
  const templates = useMessageTemplates({ enabled: connected });

  const savePreferences = useSaveMessagingPreferences();
  const saveMapping = useSaveEventMapping();
  const sendTest = useSendTestMessage();
  const retry = useRetryMessage();

  const [editing, setEditing] = useState<NotificationEvent | null>(null);
  const [statusFilter, setStatusFilter] = useState<MessageStatus | ''>('');

  const preview = useEventPreview(editing);
  const log = useMessageLog(statusFilter === '' ? {} : { status: statusFilter });

  const editingMapping = settings.data?.events.find((event) => event.event === editing) ?? null;

  useScrollToHash(!settings.isPending);

  if (settings.isPending) {
    return (
      <>
        <PageHeader title={t('notifications.title')} description={t('notifications.description')} />
        <ListSkeleton rows={4} label={t('common.loading')} />
      </>
    );
  }

  return (
    <>
      <PageHeader title={t('notifications.title')} description={t('notifications.description')} />

      <div className="flex flex-col gap-6">
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 self-start text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
          {t('notifications.back')}
        </Link>

        <ConnectionStrip connection={connection} />

        {/* --- What the shop tells its customers ------------------------------------------- */}
        <Card id="notification-events" className="scroll-mt-4">
          <CardHeader>
            <CardTitle>{t('notifications.eventsTitle')}</CardTitle>
            <CardDescription>{t('notifications.eventsDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {(settings.data?.events ?? []).map((mapping) => (
              <EventRow
                key={mapping.event}
                mapping={mapping}
                disabled={!connected}
                onEdit={() => setEditing(mapping.event)}
              />
            ))}
          </CardContent>
        </Card>

        {/* --- Quiet hours ------------------------------------------------------------------ */}
        {settings.data ? (
          <QuietHoursCard
            enabled={settings.data.quietHours.enabled}
            start={settings.data.quietHours.start}
            end={settings.data.quietHours.end}
            maxPerRecipientPerDay={settings.data.limits.maxPerRecipientPerDay}
            saving={savePreferences.isPending}
            onSave={async (next) => {
              await savePreferences.mutateAsync({
                quietHours: {
                  enabled: next.enabled,
                  start: next.start,
                  end: next.end,
                  utcOffsetMinutes: settings.data.quietHours.utcOffsetMinutes,
                },
                limits: { maxPerRecipientPerDay: next.maxPerRecipientPerDay },
              });
              toast({ title: t('notifications.preferencesSaved'), variant: 'success' });
            }}
          />
        ) : null}

        {/* --- Reminders and rework -------------------------------------------------------- */}
        {settings.data ? (
          <RemindersCard
            reminders={settings.data.reminders}
            saving={savePreferences.isPending}
            onSave={async (next) => {
              await savePreferences.mutateAsync({ reminders: next });
              toast({ title: t('notifications.preferencesSaved'), variant: 'success' });
            }}
          />
        ) : null}

        {/* --- What has already been said --------------------------------------------------- */}
        <Card id="message-log" className="scroll-mt-4">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle>{t('notifications.logTitle')}</CardTitle>
              {log.data && log.data.deadLetterCount > 0 ? (
                <Badge variant="destructive">
                  {t('notifications.deadLetterCount', { count: log.data.deadLetterCount })}
                </Badge>
              ) : null}
            </div>
            <CardDescription>{t('notifications.logDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5 sm:max-w-xs">
              <Label htmlFor="log-status">{t('notifications.filterStatus')}</Label>
              <select
                id="log-status"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as MessageStatus | '')}
                className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
              >
                <option value="">{t('notifications.filterAll')}</option>
                {(['scheduled', 'sent', 'delivered', 'read', 'failed', 'dead_letter'] as const).map(
                  (status) => (
                    <option key={status} value={status}>
                      {t(`notifications.status.${status}`)}
                    </option>
                  ),
                )}
              </select>
            </div>

            {log.isPending ? (
              <ListSkeleton rows={3} />
            ) : (log.data?.items.length ?? 0) === 0 ? (
              <EmptyState
                icon={MessageCircle}
                title={t('notifications.logEmpty')}
                description={t('notifications.logEmptyHint')}
              />
            ) : (
              <MessageLogTable
                entries={log.data?.items ?? []}
                retryingId={retry.isPending ? (retry.variables ?? null) : null}
                onRetry={(messageId) => {
                  void (async () => {
                    const sent = await retry.mutateAsync(messageId);
                    toast(
                      sent.status === 'sent'
                        ? { title: t('notifications.retrySent'), variant: 'success' }
                        : {
                            title: t('notifications.retryFailed'),
                            description: sent.error ?? undefined,
                            variant: 'destructive',
                          },
                    );
                  })();
                }}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {editingMapping ? (
        <EventMappingDialog
          mapping={editingMapping}
          templates={templates.data?.templates ?? []}
          templatesError={templates.data?.error ?? null}
          preview={preview.data}
          saving={saveMapping.isPending}
          testing={sendTest.isPending}
          onOpenChange={(open) => !open && setEditing(null)}
          onSave={async (patch) => {
            await saveMapping.mutateAsync({ event: editingMapping.event, patch });
            toast({ title: t('notifications.mappingSaved'), variant: 'success' });
          }}
          onTestSend={async (to) => {
            const sent = await sendTest.mutateAsync({ event: editingMapping.event, to });
            toast(
              sent.status === 'sent'
                ? { title: t('notifications.testSent', { to }), variant: 'success' }
                : {
                    title: t('notifications.testFailed'),
                    description: sent.error ?? undefined,
                    variant: 'destructive',
                  },
            );
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Whether anything below this line can actually send, in one line.
 *
 * It is a statement and a link, never a form: the key, the test and the disconnect all live on
 * the Integrations screen now, and offering half of them here would recreate the two-places
 * problem this move exists to end.
 *
 * The sending number is here rather than only over there because it answers the question the
 * event rows raise — "my customers will see WHAT number?" — at the moment it is asked.
 */
/**
 * Bring `#message-log` or `#notification-events` into view when the screen is arrived at by
 * anchor.
 *
 * The browser does this for a real page load and does NOT do it here: under client-side
 * routing the fragment is already in the URL by the time this component mounts, and there
 * was no document load to trigger the jump. Without these few lines the Integrations screen's
 * two links — "View message log" and "Manage messages" — both land on the top of this page,
 * which is the same destination wearing two labels.
 *
 * `ready` is the settings query having resolved: scrolling to an element that the skeleton
 * has not been replaced by yet scrolls to nothing.
 */
function useScrollToHash(ready: boolean): void {
  const { hash } = useLocation();

  useEffect(() => {
    if (!ready || !hash) return;
    // A hash is attacker-controllable only by whoever already controls the URL bar, but
    // `querySelector` on a malformed one throws, and a bad link must not blank the screen.
    const target = document.getElementById(hash.slice(1));
    target?.scrollIntoView({ block: 'start' });
  }, [hash, ready]);
}

function ConnectionStrip({
  connection,
}: {
  connection: MessagingConnection | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const connected = connection !== undefined && connection.status !== 'not_connected';

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border px-3 py-2 text-sm">
      {connection ? (
        <ConnectionStatusBadge connection={connection} />
      ) : (
        <Badge variant="outline">{t('notifications.statusNotConnected')}</Badge>
      )}

      {connected ? (
        connection.sendingNumber ? (
          <p className="text-muted-foreground">
            {t('notifications.sendingNumber')}{' '}
            <span className="font-medium text-foreground">{connection.sendingNumber}</span>
          </p>
        ) : null
      ) : (
        // Said only when it is not connected: beside a green badge the same sentence would be
        // noise, and the reason every row below is greyed out is worth a line of its own.
        <p className="text-muted-foreground">{t('notifications.connectionStripNotConnected')}</p>
      )}

      <Link to="/settings/integrations" className="ml-auto font-medium underline">
        {t('notifications.manageConnection')}
      </Link>
    </div>
  );
}

/** One notification: what it is, whether it will actually send, and a way in. */
function EventRow({
  mapping,
  disabled,
  onEdit,
}: {
  mapping: EventMapping;
  disabled: boolean;
  onEdit: () => void;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3">
      <div className="min-w-0">
        <p className="flex items-center gap-2 font-medium">
          {mapping.label}
          {/* Switched on is not the same as able to send. A shop that has enabled an event with
              no template would otherwise see a green toggle and hear nothing but silence. */}
          {mapping.ready ? (
            <Badge variant="success">{t('notifications.on')}</Badge>
          ) : mapping.enabled ? (
            <Badge variant="warning">{t('notifications.needsTemplate')}</Badge>
          ) : (
            <Badge variant="outline">{t('notifications.off')}</Badge>
          )}
        </p>
        <p className="text-sm text-muted-foreground">{mapping.description}</p>
        {mapping.templateName ? (
          <p className="text-sm text-muted-foreground">{mapping.templateName}</p>
        ) : null}
      </div>
      <Button variant="outline" disabled={disabled} onClick={onEdit}>
        {t('notifications.configure')}
      </Button>
    </div>
  );
}

/** When the shop is willing to message people, and how often one person may hear from it. */
function QuietHoursCard({
  enabled,
  start,
  end,
  maxPerRecipientPerDay,
  saving,
  onSave,
}: {
  enabled: boolean;
  start: string;
  end: string;
  maxPerRecipientPerDay: number;
  saving: boolean;
  onSave: (next: {
    enabled: boolean;
    start: string;
    end: string;
    maxPerRecipientPerDay: number;
  }) => Promise<void>;
}): ReactElement {
  const { t } = useTranslation();
  const [form, setForm] = useState({ enabled, start, end, maxPerRecipientPerDay });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('notifications.quietHoursTitle')}</CardTitle>
        <CardDescription>{t('notifications.quietHoursDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-4"
            checked={form.enabled}
            onChange={(event) => setForm({ ...form, enabled: event.target.checked })}
          />
          {t('notifications.quietHoursEnabled')}
        </label>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="quiet-start">{t('notifications.quietFrom')}</Label>
            <Input
              id="quiet-start"
              type="time"
              value={form.start}
              onChange={(event) => setForm({ ...form, start: event.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="quiet-end">{t('notifications.quietTo')}</Label>
            <Input
              id="quiet-end"
              type="time"
              value={form.end}
              onChange={(event) => setForm({ ...form, end: event.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="daily-cap">{t('notifications.dailyCap')}</Label>
            <Input
              id="daily-cap"
              type="number"
              min={1}
              max={20}
              value={form.maxPerRecipientPerDay}
              onChange={(event) =>
                setForm({ ...form, maxPerRecipientPerDay: Number(event.target.value) })
              }
            />
          </div>
        </div>

        <p className="text-sm text-muted-foreground">{t('notifications.quietHoursHint')}</p>

        <div>
          <Button disabled={saving} onClick={() => void onSave(form)}>
            {t('common.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * The two reminders that fire on a clock rather than on something happening, and the one
 * decision about rework.
 *
 * Both windows sit next to the switch that turns their event on, because the two together are
 * what actually governs whether a customer hears anything: an event switched on with a window
 * of zero sends nothing, and a window of two days on a switched-off event sends nothing either.
 * Owners look for the setting in both places, so both places have it.
 */
function RemindersCard({
  reminders,
  saving,
  onSave,
}: {
  reminders: ReminderSettings;
  saving: boolean;
  onSave: (next: ReminderSettings) => Promise<void>;
}): ReactElement {
  const { t } = useTranslation();
  const [form, setForm] = useState<ReminderSettings>(reminders);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('notifications.remindersTitle')}</CardTitle>
        <CardDescription>{t('notifications.remindersDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="delivery-lead">{t('notifications.deliveryReminderLead')}</Label>
            <Input
              id="delivery-lead"
              type="number"
              min={0}
              max={168}
              value={form.deliveryReminderLeadHours}
              onChange={(event) =>
                setForm({ ...form, deliveryReminderLeadHours: Number(event.target.value) })
              }
            />
            <p className="text-xs text-muted-foreground">
              {t('notifications.deliveryReminderLeadHint')}
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="balance-after">{t('notifications.balanceReminderAfter')}</Label>
            <Input
              id="balance-after"
              type="number"
              min={0}
              max={90}
              value={form.balanceReminderAfterDays}
              onChange={(event) =>
                setForm({ ...form, balanceReminderAfterDays: Number(event.target.value) })
              }
            />
            <p className="text-xs text-muted-foreground">
              {t('notifications.balanceReminderAfterHint')}
            </p>
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 size-4"
            checked={form.resendOnRework}
            onChange={(event) => setForm({ ...form, resendOnRework: event.target.checked })}
          />
          <span>
            {t('notifications.resendOnRework')}
            <span className="block text-xs text-muted-foreground">
              {t('notifications.resendOnReworkHint')}
            </span>
          </span>
        </label>

        <div>
          <Button disabled={saving} onClick={() => void onSave(form)}>
            {t('common.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default NotificationsPage;
